import { useEffect, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';
import usePosTypes from '../hooks/usePosTypes.js';
import { useAuth } from '../context/AuthContext.jsx';

// Distributor form. Mirrors LeadForm exactly for the contact card at
// the top, then drops the "Sales Pipeline" fieldset and adds a
// "Distributor Details" fieldset in its place. The two forms share the
// same Field component and class names so they look identical side by
// side - Dave 2026-05-31 wanted parity with the Sales Leads screen.

// Status values for the Distributor Details > Status dropdown. These
// are distributor-specific (an Esso retailer's "Demo scheduled" doesn't
// apply to a referral partner). VARCHAR(100) on the column, no CHECK.
const STATUS_OPTIONS = [
  'Active',
  'Prospect',
  'Inactive',
  'Former Partner'
];

const EMPTY = {
  // contacts (same as LeadForm)
  first_name: '', last_name: '', position: '',
  company: '',
  address1: '', address2: '',
  city: '', province_state: '', postal_zip: '', country: 'USA',
  phone_main: '', phone_cell: '', phone_fax: '',
  email1: '', email2: '', email3: '',
  website: '',
  // distributor_details
  current_status: '', number_in_chain: '',
  pos_type: '', system_type: '',
  sales_person: '', area: '', state: '', territory: '',
  passport_sites_serviced: '',
  referral_rate: '', referrals_sent: '',
  // Pitch fields - used by email templates to auto-personalise:
  //   {{position}}        - their primary contact's role (lives on contacts)
  //   {{team_function}}   - what their team does, e.g. "trains techs on Passport"
  //   {{peer_reference}}  - a peer org we can name-drop, e.g. "METCO in Wisconsin"
  //   {{their_function}}  - the short noun form, e.g. "training"
  team_function: '', peer_reference: '', their_function: '',
  last_contact: '', next_contact: '',
  // Owner_id stamped onto distributor_details. Dave 2026-06-13: Zoe
  // claims distributors as she runs outreach to set up demos.
  owner_id: ''
};

function dateOnly(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeInitial(initial) {
  if (!initial) return { ...EMPTY };
  const merged = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    if (initial[key] != null) merged[key] = initial[key];
  }
  for (const k of ['last_contact', 'next_contact']) {
    merged[k] = dateOnly(merged[k]);
  }
  return merged;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.last_name.trim() && !form.company.trim()) {
    errors.last_name = 'Last name or company is required.';
  }
  for (const k of ['email1', 'email2', 'email3']) {
    if (form[k] && !EMAIL_RE.test(form[k])) errors[k] = 'Invalid email format.';
  }
  if (form.number_in_chain && !/^\d+$/.test(String(form.number_in_chain))) {
    errors.number_in_chain = 'Must be a whole number.';
  }
  if (form.passport_sites_serviced && !/^\d+$/.test(String(form.passport_sites_serviced))) {
    errors.passport_sites_serviced = 'Must be a whole number.';
  }
  if (form.referrals_sent && !/^\d+$/.test(String(form.referrals_sent))) {
    errors.referrals_sent = 'Must be a whole number.';
  }
  if (form.referral_rate !== '' && form.referral_rate != null) {
    const n = Number(form.referral_rate);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      errors.referral_rate = 'Must be between 0 and 100.';
    }
  }
  return errors;
}

export default function DistributorForm({ initial, onSubmit, submitLabel = 'Save Changes', onCancel }) {
  const initialForm = normalizeInitial(initial);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Owner picker: admin/staff only (salespeople don't reach this form
  // -- distributors are gated to admin/staff via requireRole). Load
  // the user options when the form mounts.
  const { user } = useAuth();
  const canAssignOwner = user?.role === 'admin' || user?.role === 'staff';
  const [userOptions, setUserOptions] = useState([]);
  useEffect(() => {
    if (!canAssignOwner) return;
    let cancelled = false;
    axios.get('/api/users/options')
      .then((res) => { if (!cancelled) setUserOptions(res.data?.users || []); })
      .catch(() => { if (!cancelled) setUserOptions([]); });
    return () => { cancelled = true; };
  }, [canAssignOwner]);
  const posTypes = usePosTypes();

  function update(field) {
    return (e) => {
      const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="contact-card-form" noValidate>
      {submitError && <p className="error">{submitError}</p>}

      {/* ── Contact Card (identical structure to LeadForm) ────────── */}
      <div className="contact-card">
        <div className="contact-card-grid">
          <Field label="First Name" width={180}>
            <input value={form.first_name} onChange={update('first_name')} />
          </Field>
          <Field label="Last Name" width={180} error={errors.last_name}>
            <input
              value={form.last_name}
              onChange={update('last_name')}
              aria-invalid={!!errors.last_name}
            />
          </Field>
          <Field label="Phone Main" width={180}>
            <input value={form.phone_main} onChange={update('phone_main')} inputMode="tel" className="cell-mono" />
          </Field>
          <Field label="Phone Cell" width={150}>
            <input value={form.phone_cell} onChange={update('phone_cell')} inputMode="tel" className="cell-mono" />
          </Field>
          <Field label="Phone Fax" width={150}>
            <input value={form.phone_fax} onChange={update('phone_fax')} inputMode="tel" className="cell-mono" />
          </Field>

          <Field label="Position" width={200}>
            <input value={form.position} onChange={update('position')} />
          </Field>
          <Field label="Company" grow>
            <input value={form.company} onChange={update('company')} />
          </Field>

          <Field label="Address 1" grow>
            <input value={form.address1} onChange={update('address1')} />
          </Field>
          <Field label="Address 2" grow>
            <input value={form.address2} onChange={update('address2')} />
          </Field>

          <Field label="City" width={150}>
            <input value={form.city} onChange={update('city')} />
          </Field>
          <Field label="Province / State" width={80}>
            <input value={form.province_state} onChange={update('province_state')} />
          </Field>
          <Field label="Postal / Zip" width={100}>
            <input value={form.postal_zip} onChange={update('postal_zip')} className="cell-mono" />
          </Field>
          <Field label="Country" width={100}>
            <input value={form.country} onChange={update('country')} />
          </Field>

          <Field label="Email 1" width={220} error={errors.email1}>
            <input type="email" value={form.email1} onChange={update('email1')} className="cell-mono" aria-invalid={!!errors.email1} />
          </Field>
          <Field label="Email 2" width={220} error={errors.email2}>
            <input type="email" value={form.email2} onChange={update('email2')} className="cell-mono" aria-invalid={!!errors.email2} />
          </Field>
          <Field label="Email 3" width={220} error={errors.email3}>
            <input type="email" value={form.email3} onChange={update('email3')} className="cell-mono" aria-invalid={!!errors.email3} />
          </Field>

          <Field label="Website" width={280}>
            {/* Input + "Visit ↗" affordance. Dave 2026-06-22 — Zoe
                wants one-click access to a distributor's site without
                copy-pasting. The link normalises a missing scheme
                (e.g. "www.guardianfueltech.com" → "https://www.…").
                target=_blank + rel=noopener so it opens in a new tab
                and can't reach back into the CRM. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                value={form.website}
                onChange={update('website')}
                placeholder="https://"
                className="cell-mono"
                style={{ flex: 1 }}
              />
              {form.website && (
                <a
                  href={/^https?:\/\//i.test(form.website) ? form.website : `https://${form.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary btn-sm"
                  style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
                  title="Open website in a new tab"
                >
                  Visit ↗
                </a>
              )}
            </div>
          </Field>
        </div>
      </div>

      {/* ── Distributor Details (replaces Sales Pipeline) ─────────── */}
      <fieldset className="lead-pipeline">
        <legend>Distributor Details</legend>
        <div className="contact-card-grid">
          <Field label="Status" width={180}>
            <select value={form.current_status} onChange={update('current_status')}>
              <option value="">—</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              {form.current_status && !STATUS_OPTIONS.includes(form.current_status) && (
                <option value={form.current_status}>{form.current_status}</option>
              )}
            </select>
          </Field>
          <Field label="Territory" width={180}>
            <input value={form.territory} onChange={update('territory')} />
          </Field>
          <Field label="Area" width={150}>
            <input value={form.area} onChange={update('area')} />
          </Field>
          <Field label="State (distributor)" width={120}>
            <input value={form.state} onChange={update('state')} className="cell-mono" />
          </Field>
          <Field label="POS Type" width={180}>
            <select value={form.pos_type} onChange={update('pos_type')}>
              <option value="">—</option>
              {posTypes.filter((t) => t.active).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              {form.pos_type && !posTypes.some((t) => t.name === form.pos_type) && (
                <option value={form.pos_type}>{form.pos_type}</option>
              )}
            </select>
          </Field>
          <Field label="System Type" width={180}>
            <input value={form.system_type} onChange={update('system_type')} />
          </Field>
          <Field label="Their Sales Person" width={200}>
            <input value={form.sales_person} onChange={update('sales_person')} />
          </Field>
          {/* Owner picker -- who at XSite is working this distributor.
              Mirrors the LeadForm pattern. Dave 2026-06-13. */}
          {canAssignOwner && (
            <Field label="Owner (Assigned To)" width={220}>
              <select value={form.owner_id || ''} onChange={update('owner_id')}>
                <option value="">— Unassigned —</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.role === 'salesperson' ? ' (Salesperson)' : ` (${u.role})`}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Number in Chain" width={140} error={errors.number_in_chain}>
            <input
              type="number" min="0"
              value={form.number_in_chain}
              onChange={update('number_in_chain')}
              aria-invalid={!!errors.number_in_chain}
            />
          </Field>

          <Field label="Passport Sites Serviced" width={170} error={errors.passport_sites_serviced}>
            <input
              type="number" min="0"
              value={form.passport_sites_serviced}
              onChange={update('passport_sites_serviced')}
              aria-invalid={!!errors.passport_sites_serviced}
            />
          </Field>
          <Field label="Referral Rate (%)" width={140} error={errors.referral_rate}>
            <input
              type="number" min="0" max="100" step="0.01"
              value={form.referral_rate}
              onChange={update('referral_rate')}
              aria-invalid={!!errors.referral_rate}
            />
          </Field>
          <Field label="Referrals Sent" width={140} error={errors.referrals_sent}>
            <input
              type="number" min="0"
              value={form.referrals_sent}
              onChange={update('referrals_sent')}
              aria-invalid={!!errors.referrals_sent}
            />
          </Field>

          {/* Pitch fields - feed email-template merge tokens. Save these
              once per distributor and every outreach email picks them
              up automatically. */}
          <Field label="Team Function (for emails)" grow>
            <input
              value={form.team_function}
              onChange={update('team_function')}
              placeholder='e.g. "trains technicians on Gilbarco Passport systems"'
            />
          </Field>
          <Field label="Peer Reference (for emails)" grow>
            <input
              value={form.peer_reference}
              onChange={update('peer_reference')}
              placeholder='e.g. "METCO in Wisconsin"'
            />
          </Field>
          <Field label="Their Function (short)" width={200}>
            <input
              value={form.their_function}
              onChange={update('their_function')}
              placeholder='e.g. "training"'
            />
          </Field>

          <Field label="Last Contact" width={150}>
            <DateInput value={form.last_contact} onChange={update('last_contact')} />
          </Field>
          <Field label="Next Contact" width={150}>
            <DateInput value={form.next_contact} onChange={update('next_contact')} />
          </Field>
        </div>
      </fieldset>

      <div className="form-actions contact-card-actions">
        <span className="form-hint">
          Fields marked <span className="req">*</span> are required.
        </span>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

// Same Field component as LeadForm - keeps the two forms visually
// identical. Copied (not imported) so LeadForm changes don't leak here
// without explicit review.
function Field({ label, children, width, grow, required, error }) {
  const style = grow
    ? { flex: '1 1 200px', minWidth: 0 }
    : { flex: `0 0 ${width || 220}px`, width: width || 220, minWidth: 0 };
  return (
    <label
      className={`form-field${error ? ' form-field-error' : ''}`}
      style={style}
    >
      <span>
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
